/* eslint-disable quotes */
/* eslint func-style: "off" */
/* eslint no-empty-pattern: "off" */
import { EventTypes } from 'redux-segment';
import {
  all,
  call,
  fork,
  put,
  select,
  take,
  takeLatest,
  cancel} from 'redux-saga/effects';
import { v1 } from 'uuid';
import moment from 'moment';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import each from 'lodash/each';

import { convertDatePickerTimeToApiTime,  } from 'helpers/timeFormat';
import apiClient from 'helpers/apiClient';
import { INTERVAL_OPTIONS } from './components/HistoricsLineChart/helpers';
import { TWITTER_SUBTYPE_OPTIONS, STORIES_PAGE_SIZE, MEDIA_OPTIONS_SHORTENED, convertAdditionalFiltersArrToStr, convertSelectedLanguagesToStr } from './helpers';
import { convertEnabledKeywordsToStr } from './helpers';

import {
  FETCH_TRANSLATIONS,
  FETCH_SEARCH_RESULTS,
  FETCH_RECENT_SEARCH_RESULTS,
  FETCH_RECENT_SEARCHES,
  UPDATE_SEARCH_HISTORY,
  CLEAR_SEARCH_RESULTS,
  CLEAR_RECENT_SEARCHES,
  FETCH_MORE_STORIES,
  FETCH_SEARCH_INTERVAL,
  FETCH_NEW_STORIES,
  SAVE_AS_PROFILE,
  fetchSearchResultsSucceeded,
  fetchTranslationsSucceded,
  fetchRecentSearchesSucceeded,
  fetchMoreStoriesSucceeded,
  fetchNewStoriesSucceeded,
  fetchFailed,
  setTranslationNotice,
  fetchTranslationsFailed,
  clearUserHistorySucceeded,
  shouldScroll
} from './DiscoverHistorics.ducks';
import {
  setEnabledKeywordsCount,
  setFFBParseErrors,
  setZqlArray
} from './containers/HistoricsSearchForm/HistoricsSearchForm.ducks';

import { setTranslations, clearTranslations } from './containers/HistoricsSearchForm/HistoricsSearchForm.ducks';
import _, { isEmpty } from 'lodash';

const DISCOVER_HISTORY_LIMIT = 50;
const VALID_INTERVALS = {
  '1 min': '1m',
  '15 min': '15m'
};

// Selectors
const getEndDate = (state) => state.DiscoverHistorics.HistoricsSearchForm.endDate;
const getSearchTerms = (state) => state.DiscoverHistorics.HistoricsSearchForm.searchTerms;
const getSelectedLanguages = (state) => state.DiscoverHistorics.HistoricsSearchForm.selectedLanguages;
const getTranslations = (state) => state.DiscoverHistorics.HistoricsSearchForm.translations;
const getAreTranslationsObsolete = (state) => state.DiscoverHistorics.HistoricsSearchForm.areTranslationsObsolete;
const getEnabledKeywordsCount = (state) => state.DiscoverHistorics.HistoricsSearchForm.enabledKeywordsCount;
const getStartDate = (state) => state.DiscoverHistorics.HistoricsSearchForm.startDate;
const getLastSearchEndDate = (state) => state.DiscoverHistorics.Page.lastSearchEndDate;
const getLastSearchStartDate = (state) => state.DiscoverHistorics.Page.lastSearchStartDate;
const getTimezone = (state) => state.AppUser.user.timezone;
const getStories = (state) => state.DiscoverHistorics.Page.stories;
const getHasMoreStories = (state) => state.DiscoverHistorics.Page.hasMoreStories;
const getSelectedIntervalIndex = (state) => state.DiscoverHistorics.Page.intervalSelectionIndex;
const getSourceFilter = (state) => state.DiscoverHistorics.Page.selectedSource;
const getHasFetchErrors = (state) => state.DiscoverHistorics.Page.hasFetchErrors;
const getAdditionalFilters = (state) => state.DiscoverHistorics.HistoricsSearchForm.additionalFilters;
// Avoid state mutations impacting the store directly.
const getUser = (state) => cloneDeep(state.AppUser.user);
const getPrivacyMode = (state) => state.DiscoverHistorics.Page.privacyMode;

// URLs
const TIME_SERIES_URL = '/api/discover-time-series';
const STORIES_URL = '/api/discover-stories';
const TRANSLATION_URL = '/api/translate/keywords';
const FFB_TRANSLATION_URL = '/zql-translation/v2/translate/ffb';

// eslint-disable-next-line max-params
const _storiesRequest = ({ zqlArray, startTime, endTime, from }) => ({
  method: 'POST',
  body: JSON.stringify({ zqlArray, startTime, endTime, from, size: STORIES_PAGE_SIZE })
});

const _pendoPanelProps = (event, searchTerms, selectedLanguages, additionalFilters, start, end) => {
  const lang = _(selectedLanguages).flatten().map('code').uniq().value();
  const activeFilters = _(additionalFilters).map('filters').compact().value();
  const twitterContent = _(activeFilters).map('twitterContent').compact().flatten().uniq().value();
  const mediaOptions = _(activeFilters).filter('mediaChecked').map('mediaOption').sort().uniq()
    .map((num) => MEDIA_OPTIONS_SHORTENED[num]).value();
  const excludeUSContent = _.some(activeFilters, 'excludeUSContent');
  const numOfdays = moment(end).diff(moment(start), 'days') + 1;
  const numOfSearches = searchTerms.length;

  // only track when user has a search term
  if (numOfSearches === 0) { return null; }
  const properties = {
    searchTerms,
    lang,
    twitterContent,
    mediaOptions,
    excludeUSContent,
    start,
    end,
    numOfdays,
    numOfSearches,
  };
  return {
    analytics: {
      eventType: EventTypes.track,
      eventPayload: {
        event,
        properties
      }
    }
  };
};

// applies the twitter content (subtypes) filter to the search term, i.e. 'twitter_type' ffb target
const _applyTwitterContentFilters = (searchTerm, settings, allSources) => {
  let filteredSearchTerm = searchTerm;
  if (settings.filters) {
    const otherSources = allSources.filter((source) => source !== 'twitter');
    const allSourcesFFB = `(source:(${allSources.join(' OR ')})`;
    const otherSourceFFB = `(source:(${otherSources.join(' OR ')})`;
    const { twitterContent } = settings.filters;
    // if all checkboxes are selected, we don't need to change the zql
    if (twitterContent && twitterContent.length !== TWITTER_SUBTYPE_OPTIONS.length) {
      // to use twitter_type, we need to split this into two statements with
      // twitter and twitter_type separated from the other sources
      filteredSearchTerm = `(${allSourcesFFB} AND (${otherSourceFFB} AND ${searchTerm})))`;
      if (twitterContent.length) {
        filteredSearchTerm += ` OR (${allSourcesFFB} AND (source:(twitter) AND (twitter_type:(${twitterContent.join(' OR ')}) AND ${searchTerm})))))`;
        filteredSearchTerm = `(${filteredSearchTerm})`;
      }
    }
  }
  return filteredSearchTerm;
};

// applies the media filters to the search term, i.e. 'content_type' ffb target
const _applyMediaFilters = (searchTerm, settings, allSources) => {
  if (settings.filters) {
    const { mediaChecked, mediaOption='0' } = settings.filters;
    if (mediaChecked) {
      const allSourcesFFB = `(source:(${allSources.join(' OR ')})`;
      // default options here are for mediaOption == 3
      let options = ['video', 'photo'];
      let operator = 'AND';
      if (mediaOption == 0) { operator = 'AND NOT'; }
      else if (mediaOption == 1) { options = ['photo']; }
      else if (mediaOption == 2) { options = ['video']; }
      const contentTypeFFB = `(content_type:(${options.join(' OR ')})))`;
      searchTerm = `${allSourcesFFB} AND ${searchTerm} ${operator} ${contentTypeFFB}`;
    }
  }
  return searchTerm;
};

// applies the exlcudeUS filter to the search term
const _applyExcludeUSFilter = (searchTerm, settings) => {
  if (settings.filters) {
    const { excludeUSChecked } = settings.filters;
    searchTerm = excludeUSChecked ? `${searchTerm} AND NOT country:US` : searchTerm;
  }
  return searchTerm;
};

const _saveSearchHistory = function* (userId, searchTerms, startDate, endDate, settings) {
  const url = '/api/discover-search-history';
  const fetchOptions = {
    method: 'POST',
    body: JSON.stringify({
      userId,
      searchTerms,
      startDate,
      endDate,
      settings
    })
  };
  return yield call(apiClient, url, fetchOptions);
};

// For tracking how long Discover API calls are taking
const _setPendopanelQueryTimer = (timeStart, timeEnd, pendoPanelData) => {
  const props = get(pendoPanelData, 'analytics.eventPayload.properties');
  if (props) {
    props.queryTimeTotal = timeEnd - timeStart; // Elapsed time in milliseconds
  }
};

const _getIntervalOptions = (startDate, endDate) => {
  const millisecondDiff = moment(endDate).diff(moment(startDate));
  const filteredOptions = INTERVAL_OPTIONS.filter(({milliseconds}) => {
    const pointsOnGraph = millisecondDiff / milliseconds;
    // we only want to show reasonable interval options for user, at least 2 points on the graph, but less than 1800
    return pointsOnGraph > 2 && pointsOnGraph < 1800;
  });
  return filteredOptions.length ? filteredOptions : [INTERVAL_OPTIONS[3]];
};


// Get recent searches
export const fetchRecentSearches = function* () {
  try {
    const privacyMode = yield select(getPrivacyMode);
    if (privacyMode) { return; }

    const user = yield select(getUser);
    const emptyHistory = {
      userId: user.id,
      history: []
    };

    const url = `/api/discover-search-history/user/${user.id}`;
    const fetchOptions = {
      method: 'GET',
    };

    const recentSearches = yield call(apiClient, url, fetchOptions);

    yield put(fetchRecentSearchesSucceeded(recentSearches || emptyHistory));
  } catch (e) {
    console.log(e);
  }
};

export const fetchRecentSearchesSaga = function* () {
  while (true) {
    const backgroundFetch = yield takeLatest(FETCH_RECENT_SEARCHES, fetchRecentSearches);
    yield take(CLEAR_RECENT_SEARCHES);
    yield cancel(backgroundFetch);
  }
};

// Save recent search and return the results
export const updateSearchHistory = function* () {
  try {
    const privacyMode = yield select(getPrivacyMode);
    if (privacyMode) { return; }

    const timezone = yield select(getTimezone);
    const searchTerms = yield select(getSearchTerms);
    const pickerStartDate = yield select(getStartDate);
    const pickerEndDate = yield select(getEndDate);
    const user = yield select(getUser);
    const startDate = convertDatePickerTimeToApiTime(pickerStartDate, timezone);
    const endDate = convertDatePickerTimeToApiTime(pickerEndDate, timezone);
    const additionalFilters = yield select(getAdditionalFilters);
    const selectedLanguages = yield select(getSelectedLanguages);
    const translations = yield select(getTranslations);
    const settings = {
      additionalFilters: convertAdditionalFiltersArrToStr(additionalFilters).slice(0, searchTerms.length),
      selectedLanguages: convertSelectedLanguagesToStr(selectedLanguages).slice(0, searchTerms.length),
      enabledKeywords: convertEnabledKeywordsToStr(translations).slice(0, searchTerms.length)
    };
    const updatedRecentSearches = yield call(
      _saveSearchHistory, user.id, searchTerms, startDate, endDate, settings
    );
    yield put(fetchRecentSearchesSucceeded(updatedRecentSearches));
  } catch (e) {
    yield put(fetchFailed(e.message));
  }
};

export const updateSearchHistorySaga = function* () {
  const backgroundFetch = yield takeLatest(UPDATE_SEARCH_HISTORY, updateSearchHistory);
  yield take(CLEAR_RECENT_SEARCHES);
  yield cancel(backgroundFetch);
};

// recreate query terms with translations
// pick every translated keyword and apply the translations to the search term
// creating ( original OR translation1 OR translation1 ... ) clauses
const _addTranslationsToSearchTerms = (translations, originalSearchTerms) => {
  // original keywords
  const keywords = translations[0].keywords.map((keyword) => ({ id: keyword.keywordId, text: keyword.text}));
  let initalSearchTerm = originalSearchTerms.slice();
  let translatedSearchTerm = '';

  // for every translated keyword / phrase, we add the original keyword and the translations
  keywords.forEach((keyword) => {
    const keywordTranslations = translations.reduce((acc, language) => {
      const translation = language.keywords.find(keywordData => keyword.id === keywordData.keywordId);
      if (translation && translation.enabled && translation.text) {
        acc.push(translation.text);
      }
      return acc;
    }, []);

    if (isEmpty(keywordTranslations)) {
      throw new Error('For every term in the search box at least one language (translation or original) needs to be enabled. If the term should not be searched, it needs to be removed from the search box.', { cause: 'translation' });
    }

    const keywordIndex = initalSearchTerm.indexOf(keyword.text);
    if (keywordIndex > -1) {
      let startIndex = keywordIndex;
      let endIndex = keywordIndex + keyword.text.length;
      let prePosFix = '';

      // if the keyword is inside quotes, we need to add the quotes to the translation
      if (keywordIndex > 0 && initalSearchTerm[keywordIndex - 1] === '"' ) {
        prePosFix = '"';
        startIndex -= 1;
        endIndex += 1;
      }
      const addParens = keywordTranslations.length > 1;
      const translationsJoin = keywordTranslations.join(`${prePosFix} OR ${prePosFix}`);
      translatedSearchTerm += initalSearchTerm.slice(0, startIndex);
      translatedSearchTerm += `${addParens?'(':''}${prePosFix}${translationsJoin}${prePosFix}${addParens?')':''}`;
      initalSearchTerm = initalSearchTerm.slice(endIndex);
    }
  });
  // concat the rest of the query after the last keyword
  translatedSearchTerm += initalSearchTerm;
  return translatedSearchTerm;
};

// limits the all sources query to the source picked in the "All sources" dropdown. The
// source is ANDed around the all sources query, so the twitter content filter still
// applies when twitter is picked
const _applySourceSelection = (searchTerm, settings, availableSources, selectedSource) => {
  const allSourcesSearchTerm = _applyTwitterContentFilters(searchTerm, settings, availableSources);
  if (!selectedSource || selectedSource === 'allSources') {
    return allSourcesSearchTerm;
  }
  // a selection that isn't an account source is a social platform
  const isPlatform = !availableSources.includes(selectedSource);
  const sourceFFB = isPlatform
    ? `(platform:${selectedSource} AND source:social_platform)`
    : `source:(${selectedSource})`;
  return `(${sourceFFB} AND ${allSourcesSearchTerm})`;
};

const _addAdditionalFiltersToSearchTerms = ({
  availableSources,
  selectedSource,
  searchTerm,
  additionalFiltersSettings
}) => {
  let parsedSearchTerm = searchTerm && _applySourceSelection(searchTerm, additionalFiltersSettings, availableSources, selectedSource);
  parsedSearchTerm = parsedSearchTerm && _applyMediaFilters(parsedSearchTerm, additionalFiltersSettings, availableSources);
  parsedSearchTerm = parsedSearchTerm && _applyExcludeUSFilter(parsedSearchTerm, additionalFiltersSettings);
  return parsedSearchTerm;
};

const _parseSearchTerms = (searchTerm, configOptions) => {
  const {
    selectedSource,
    additionalFiltersSettings,
    translations,
    areTranslationsObsolete
  } = configOptions;

  return new Promise((resolve) => {
    // Twitter is always available in Discover regardless of account dataSources
    const availableSources = uniq(['twitter', ...window.Helper.getAccountSources()]);
    const originalSearchTerm = searchTerm;
    // Parentheses are necessary here in case searchTerms are using OR
    searchTerm = `(${searchTerm})`;
    let parsedSearchTerm = searchTerm;

    if (!isEmpty(translations) && !areTranslationsObsolete) {
      parsedSearchTerm = _addTranslationsToSearchTerms(translations, parsedSearchTerm);
    }

    parsedSearchTerm = _addAdditionalFiltersToSearchTerms({
      availableSources,
      selectedSource,
      searchTerm: parsedSearchTerm,
      additionalFiltersSettings
    });

    // we can remove the parentheses if no other filters were applied
    parsedSearchTerm = parsedSearchTerm === `(${originalSearchTerm})` ? originalSearchTerm : parsedSearchTerm;

    // Use backend endpoint to translate FFB to ZQL
    const fetchOptions = {
      method: 'POST',
      body: JSON.stringify({
        input: {
          ffb: parsedSearchTerm,
          source_types: availableSources,
          skip_source_validation: true // but keep server-side source scoping
        },
        outputs: ['zql']
      })
    };
    apiClient(FFB_TRANSLATION_URL, fetchOptions)
      .then((response) => {
        // Expecting response to contain zql and optional errors
        const zql = response?.zql;
        const err = response?.errors || null;
        resolve({
          zql,
          err,
          ffbQuery: parsedSearchTerm
        });
      })
      .catch((error) => {
        resolve({
          zql: null,
          err: error,
          ffbQuery: parsedSearchTerm
        });
      });
  });
};

// useSelectedSource: false builds the all sources query (the time-series and the dropdown
// counts never change with the dropdown), true limits it to the dropdown's source (stories)
const _getQueryBody = function* (useSelectedSource = false) {
  const timezone = yield select(getTimezone);
  const searchTerms = yield select(getSearchTerms);
  const pickerStartDate = yield select(getStartDate);
  const pickerEndDate = yield select(getEndDate);
  const selectedSource = yield select(getSourceFilter);
  const selectedLanguages = yield select(getSelectedLanguages);
  const additionalFilters = yield select(getAdditionalFilters);
  const translations = yield select(getTranslations);
  const areTranslationsObsolete = yield select(getAreTranslationsObsolete);

  const startDate = convertDatePickerTimeToApiTime(pickerStartDate, timezone);
  const endDate = convertDatePickerTimeToApiTime(pickerEndDate, timezone);
  const intervalOptions = _getIntervalOptions(startDate, endDate);
  const intervalSelectionIndex = yield select(getSelectedIntervalIndex);
  const { label } = intervalOptions[intervalSelectionIndex];
  const validInterval = Object.keys(VALID_INTERVALS).includes(label) ? VALID_INTERVALS[label] : label;

  const zqlArray = yield Promise.all(searchTerms.map((searchTerm, i) => {
    return _parseSearchTerms(searchTerm, {
      selectedSource: useSelectedSource ? selectedSource : 'allSources',
      additionalFiltersSettings: additionalFilters[i],
      translations: translations[i],
      areTranslationsObsolete: areTranslationsObsolete[i]
    });
  }));

  return {
    zqlArray,
    validInterval,
    startDate,
    endDate,
    searchTerms,
    intervalOptions,
    intervalSelectionIndex,
    selectedSource,
    selectedLanguages,
    additionalFilters
  };
};

export const fetchSearchResults = function* () {
  let meta;
  try {
    const {
      zqlArray,
      validInterval,
      startDate,
      endDate,
      searchTerms,
      intervalOptions,
      intervalSelectionIndex,
      selectedLanguages,
      additionalFilters
    } = yield _getQueryBody();
    // if there is an error then we won't search
    const errorCount = zqlArray.filter((zo) => zo.err).length;

    // this will put the parser errors on the store and will display them
    // in the discover2 UI, coordinate with Duncan for the design.
    yield put(setFFBParseErrors(zqlArray));

    if (errorCount > 0) {
      throw new Error('Invalid search terms');
    }

    yield put(setZqlArray({zqlArray}));

    // pendo panel tracking
    meta = _pendoPanelProps('Discover Historics Search', searchTerms, selectedLanguages, additionalFilters, startDate, endDate);

    yield updateSearchHistory();

    const fetchOptions = {
      method: 'POST',
      body: JSON.stringify({
        zqlArray,
        interval: validInterval,
        startTime: startDate,
        endTime: endDate
      })
    };

    yield put(shouldScroll());

    const [timeSeriesCounts, storiesResults] = yield all([
      call(apiClient, TIME_SERIES_URL, fetchOptions),
      call(apiClient, STORIES_URL, _storiesRequest({ zqlArray, startTime: startDate, endTime: endDate, from: 0 }))
    ]);

    const hasData = timeSeriesCounts.some(e => e.total > 0);
    const stories = storiesResults.data || [];
    if (hasData) {
      const searchResultsPayload = {
        timeSeriesCounts,
        stories,
        intervalOptions,
        intervalSelectionIndex,
        searchTerms,
      };

      yield put(fetchSearchResultsSucceeded(searchResultsPayload, meta));
    } else {
      const payload = {
        title: 'No results found',
        body: 'Please try another search.',
        type: 'info'
      };
      yield put(fetchFailed(payload, meta));
    }
  } catch (e) {
    // try to get error from body if it's there, otherwise use the message
    get(meta, 'analytics.eventPayload.properties', {}).error = get(e, 'body.error', e.message);
    const payload = e.message === 'Invalid search terms' ? null : {
      title: 'Could not fetch search results',
      body: e.cause === 'translation' ? e.message : `Error: A problem has ocurred. Please try again later.`,
      type: 'error'
    };
    yield put(fetchFailed(payload, meta));
  }
};

// the enabled flags are encoded positionally, one character per keyword and one
// comma separated segment per language. A language or a keyword the translation
// service can no longer handle shifts every position after it, so a saved search
// or a shared url created before that happened would disable the wrong keyword
const _matchesTranslationShape = (keywordsJoinStr, translations = []) => {
  if (typeof keywordsJoinStr !== 'string') {
    return false;
  }
  const segments = keywordsJoinStr.split(',');
  return segments.length === translations.length
    && segments.every((segment, langIndex) => (
      segment.length === get(translations, [langIndex, 'keywords', 'length'])
    ));
};

export const fetchRecentSearchResults = function* ({payload: enabledKeywords}) {
  try {
    const searchTerms = yield select(getSearchTerms);
    const selectedLanguages = yield select(getSelectedLanguages);

    const translationCalls = [];
    for (let i = 0; i < searchTerms.length; i++) {
      const languages = selectedLanguages[i];
      if (languages && languages[0]) {
        translationCalls.push(_fetchTranslations({ payload: { index: i, languages: selectedLanguages[i], searchTerm: searchTerms[i] } }));
      } else {
        translationCalls.push({ translations: [], enabledKeywordsCount: 0 });
      }
    }
    const tranlationResults = yield all(translationCalls);
    const newTranslations = [[],[],[],[],[]];
    const newEnabledKeywordsCount = [];
    // a restored search can hold up to five terms, they share one notice
    const unsupportedLanguages = [];
    const failedLanguages = [];
    for (let i = 0; i < searchTerms.length; i++) {
      const { translations, enabledKeywordsCount } = tranlationResults[i];
      newTranslations[i] = translations;
      newEnabledKeywordsCount[i] = enabledKeywordsCount;
      unsupportedLanguages.push(...get(tranlationResults[i], 'unsupportedLanguages', []));
      failedLanguages.push(...get(tranlationResults[i], 'failedLanguages', []));
    }
    each(enabledKeywords, (keywordsJoinStr, searchIndex) => {
      // the flags no longer line up with the translations we just built, applying
      // them would silently change the query, so leave every keyword enabled
      if (!_matchesTranslationShape(keywordsJoinStr, newTranslations[searchIndex])) {
        return;
      }
      each(keywordsJoinStr.split(','), (keywordsStr, langIndex) => {
        each(keywordsStr, (number, keywordIndex) => {
          if (number === '0') {
            try {
              newTranslations[searchIndex][langIndex].keywords[keywordIndex].enabled = false;
              newEnabledKeywordsCount[searchIndex]--;
            } catch(e) {}
          }
        });
      });
    });
    yield put(fetchTranslationsSucceded());
    yield put(setTranslations({translations: newTranslations}));
    yield put(setEnabledKeywordsCount(newEnabledKeywordsCount));
    yield fetchSearchResults();

    // the user never picked these languages in this session, so a language the
    // service dropped has to be reported. It waits until the search is done
    // because a successful search resets the banner state, and a search error
    // is the more important message, so it wins
    if (!isEmpty(unsupportedLanguages) || !isEmpty(failedLanguages)) {
      const hasFetchErrors = yield select(getHasFetchErrors);
      if (!hasFetchErrors) {
        yield put(setTranslationNotice(_translationNotice(
          unsupportedLanguages, failedLanguages, newTranslations.some(t => !isEmpty(t))
        )));
      }
    }
  } catch (err) {
    yield put(fetchTranslationsFailed(_translationErrorNotice(err)));
  }
};

export const saveAsProfile = function* ({ payload: { searchTerm, metadata } }) {
  try {
    const { index, discoverStartDate, activeCampaignId } = metadata;
    const additionalFilters = yield select(getAdditionalFilters);
    const translations = yield select(getTranslations);
    const areTranslationsObsolete = yield select(getAreTranslationsObsolete);

    if (!isEmpty(translations[index]) && areTranslationsObsolete[index]) {
      throw new Error('Please renew or remove translations before saving as dataset');
    }

    const { ffbQuery }  = yield call(_parseSearchTerms, searchTerm, {
      // a dataset is always the all sources query
      selectedSource: 'allSources',
      additionalFiltersSettings: additionalFilters[index],
      translations: translations[index],
      areTranslationsObsolete: areTranslationsObsolete[index]
    });

    window.localStorage.setItem('profileDefinition', ffbQuery);
    window.localStorage.setItem('discoverStartDate', discoverStartDate);
    const profilesUrlDomain = 'data';
    window.location.href = `/${profilesUrlDomain}/campaigns/${activeCampaignId}/profiles/new/?source=discover`;
  } catch (e) {
    const payload = {
      title: 'Could not save as dataset',
      body: e.message ? e.message : `Error: A problem has ocurred. Please try again later.`,
      type: 'error'
    };
    yield put(fetchFailed(payload));
  }
};

// Translation helpers
//
// error strings returned by POST /api/translate/keywords - a contract shared
// with Bacon's TranslateService.ts. Any other error ('Translation failed',
// 'Keyword is too long to translate') is a per keyword failure, not a language
// the translation service cannot handle at all
const LANGUAGE_NOT_SUPPORTED = 'Language not supported';

const _isKeywordTarget = (zql) => zql.target && zql.target === 'text';
const _generateKeywords = (zql) => {
  let keywordMap = [];

  // this is a keyword, aggregate it
  if (_isKeywordTarget(zql)) {
    return zql.arguments.map(elem => {
      return {
        id: v1(),
        text: elem
      };
    });
  }

  // NOT uses argument instead of arguments
  if (!_isKeywordTarget(zql) && zql.argument) {
    keywordMap = [ ...keywordMap, ..._generateKeywords(zql.argument) ];
    return keywordMap;
  }

  // halting case: this is not a keyword and I dont have any more arguments
  if (!_isKeywordTarget(zql) && (!zql.arguments || !zql.arguments.length)) {
    return [];
  }

  // recursive call
  zql.arguments.forEach(element => {
    keywordMap = [ ...keywordMap, ..._generateKeywords(element) ];
  });
  return keywordMap;
};

const _generateTranslationState = (languages, translationResults) => {
  // generate ORIGINAL keywords list
  const originalLanguage = { label: 'Original', code: 'ZIGNAL' };
  const originalKeywords = translationResults.map(tr => {
    return { keywordId: tr.id, text: tr.text, enabled: true };
  });
  // generate Translations keywords list
  // a keyword the service could not translate is dropped, and a language left
  // without any keyword is dropped altogether and reported back to the caller
  const unsupportedLanguages = [];
  const failedLanguages = [];
  const translationsByLanguage = [];
  languages.forEach(lang => {
    const language = { label: lang.label, code: lang.code };
    let isUnsupported = false;
    let hasFailedKeyword = false;
    const keywords = translationResults.reduce((acc, tr) => {
      const target = (get(tr, 'translations') || []).find(t => t.code === lang.code);
      if (target && !target.error && target.translation) {
        acc.push({ keywordId: tr.id, text: target.translation, enabled: true });
      } else if (get(target, 'error') === LANGUAGE_NOT_SUPPORTED) {
        isUnsupported = true;
      } else {
        // a transient failure, an over long keyword, or a term the service did
        // not answer for at all: the language itself may still work
        hasFailedKeyword = true;
      }
      return acc;
    }, []);
    // lang.name is the display name ('Basque'), label is the upper cased code ('EU')
    const name = lang.name || lang.label || lang.code;
    if (isUnsupported) {
      unsupportedLanguages.push(name);
    } else if (hasFailedKeyword) {
      failedLanguages.push(name);
    }
    if (!isEmpty(keywords)) {
      translationsByLanguage.push({ language, keywords });
    }
  });
  return {
    // when no language survived there is nothing to show: prepending the Original
    // row would render a translation block the user has no way to remove
    translations: isEmpty(translationsByLanguage) ? [] : [
      { language: originalLanguage, keywords: originalKeywords },
      ...translationsByLanguage
    ],
    unsupportedLanguages,
    failedLanguages
  };
};

// non blocking banner listing the languages the translation service could not handle
const LANGUAGES_SHOWN_IN_NOTICE = 8;
const _languageList = (names) => {
  const overflow = names.length - LANGUAGES_SHOWN_IN_NOTICE;
  return overflow > 0
    ? `${names.slice(0, LANGUAGES_SHOWN_IN_NOTICE).join(', ')} and ${overflow} more`
    : names.join(', ');
};

const _translationNotice = (unsupportedLanguages, failedLanguages, hasTranslations) => {
  // a restored search reports every one of its terms, so the same language can
  // arrive more than once, and can even land in both buckets
  const unsupported = uniq(unsupportedLanguages);
  const failed = uniq(failedLanguages).filter(name => !unsupported.includes(name));
  const sentences = [];
  if (!isEmpty(unsupported)) {
    sentences.push(`Translation is not available for: ${_languageList(unsupported)}.`);
  }
  if (!isEmpty(failed)) {
    // these languages are supported, the service just could not translate every
    // keyword for them, so they must not be reported as unavailable
    sentences.push(`Some keywords could not be translated: ${_languageList(failed)}.`);
  }
  if (hasTranslations) {
    sentences.push('The remaining languages were translated.');
  }
  return {
    title: isEmpty(unsupported)
      ? 'Some keywords could not be translated'
      : 'Some languages could not be translated',
    body: sentences.join(' '),
    type: 'info'
  };
};

// FFB parse errors are already surfaced by setFFBParseErrors, so they get no banner
const _translationErrorNotice = (err) => {
  if (get(err, 'message') === 'Invalid search terms') {
    return null;
  }
  return {
    title: 'Could not translate keywords',
    body: `Error: A problem has ocurred. Please try again later.`,
    type: 'error'
  };
};

const _fetchTranslations = function* ({ payload: { index, languages, searchTerm } }) {
  const selectedLanguages = yield select(getSelectedLanguages);
  const zqlFromTerms = yield call(_parseSearchTerms, searchTerm, {
    selectedSource: 'allSources',
    additionalFiltersSettings: [{},{},{},{},{}],
  });

  // this will put the parser errors on the store and will display them
  yield put(setFFBParseErrors([zqlFromTerms]));

  if (zqlFromTerms.err) {
    throw new Error('Invalid search terms');
  }

  // extract translation-able keywords from the zql
  const terms = _generateKeywords(zqlFromTerms.zql);
  const targetLanguages = isEmpty(languages) ? selectedLanguages[index] : languages;

  const fetchOptions = {
    method: 'POST',
    body: JSON.stringify({
      languages: targetLanguages.map(lang => lang.code),
      terms
    })
  };

  // fetch the translations and reformat the response into the correct state
  const translationResults = yield call(apiClient, TRANSLATION_URL, fetchOptions);
  const {
    translations, unsupportedLanguages, failedLanguages
  } = _generateTranslationState(targetLanguages, translationResults);
  // amount of keywords = every keyword that survived translation, across the original and every language
  // every keyword starts enabled
  const enabledKeywordsCount = translations.reduce((acc, { keywords }) => acc + keywords.length, 0);
  return { translations, enabledKeywordsCount, unsupportedLanguages, failedLanguages };
};

export const fetchTranslations = function* ({ payload }) {
  const { index} = payload;
  try {
    const {
      translations, enabledKeywordsCount, unsupportedLanguages, failedLanguages
    } = yield _fetchTranslations({ payload });
    const translationsState = yield select(getTranslations);
    const enabledKeywordsCountState = yield select(getEnabledKeywordsCount);

    const newTranslations = translationsState.slice();
    const newEnabledKeywordsCount = enabledKeywordsCountState.slice();
    newTranslations[index] = translations;
    newEnabledKeywordsCount[index] = enabledKeywordsCount;

    yield put(fetchTranslationsSucceded());
    yield put(setTranslations({ index, translations: newTranslations }));
    yield put(setEnabledKeywordsCount(newEnabledKeywordsCount));

    if (!isEmpty(unsupportedLanguages) || !isEmpty(failedLanguages)) {
      yield put(setTranslationNotice(
        _translationNotice(unsupportedLanguages, failedLanguages, !isEmpty(translations))
      ));
    }
  } catch (err) {
    yield put(fetchTranslationsFailed(_translationErrorNotice(err)));
    yield put(clearTranslations({index}));
  }
};

export const fetchSearchInterval = function* () {
  let meta;
  try {
    const {
      zqlArray,
      validInterval,
      startDate,
      endDate,
      searchTerms,
      intervalOptions,
      intervalSelectionIndex,
      selectedLanguages,
      additionalFilters
    } = yield _getQueryBody();
    // if there is an error then we won't search
    const errorCount = zqlArray.filter((zo) => zo.err).length;

    // this will put the parser errors on the store and will display them
    // in the discover2 UI, coordinate with Duncan for the design.
    yield put(setFFBParseErrors(zqlArray));

    if (errorCount > 0) {
      throw new Error('Invalid search terms');
    }

    // pendo panel tracking
    meta = _pendoPanelProps('Discover Historics Search', searchTerms, selectedLanguages, additionalFilters, startDate, endDate);

    const fetchOptions = {
      method: 'POST',
      body: JSON.stringify({
        zqlArray,
        interval: validInterval,
        startTime: startDate,
        endTime: endDate,
      })
    };

    const timeSeriesCounts = yield call(apiClient, TIME_SERIES_URL, fetchOptions);

    const hasData = timeSeriesCounts.some(e => e.total > 0);
    // only the counts changed, keep the stories already listed
    const stories = yield select(getStories);

    if (hasData) {
      yield put(fetchSearchResultsSucceeded({
        timeSeriesCounts,
        stories,
        intervalOptions,
        intervalSelectionIndex,
        searchTerms
      }, meta));
    } else {
      const payload = {
        title: 'No results found',
        body: 'Please try another search.',
        type: 'info'
      };
      yield put(fetchFailed(payload, meta));
    }
  } catch (e) {
    // try to get error from body if it's there, otherwise use the message
    get(meta, 'analytics.eventPayload.properties', {}).error = get(e, 'body.error', e.message);
    const payload = e.message === 'Invalid search terms' ? null : {
      title: 'Could not fetch search results',
      body: `Error: A problem has ocurred. Please try again later.`,
      type: 'error'
    };
    yield put(fetchFailed(payload, meta));
  }
};

export const fetchNewStories = function* ({ payload: { startDate, endDate, lineChartBackDisabled }}) {
  try {
    const {
      zqlArray,
      intervalOptions,
      intervalSelectionIndex,
      searchTerms
    } = yield _getQueryBody(true);

    // if there is an error then we won't search
    const errorCount = zqlArray.filter((zo) => zo.err).length;

    // this will put the parser errors on the store and will display them
    // in the discover2 UI, coordinate with Duncan for the design.
    yield put(setFFBParseErrors(zqlArray));

    if (errorCount > 0) {
      throw new Error('Invalid search terms');
    }

    yield put(setZqlArray({zqlArray}));

    const storiesResults = yield call(
      apiClient,
      STORIES_URL,
      _storiesRequest({ zqlArray, startTime: startDate, endTime: endDate, from: 0 })
    );
    const stories = storiesResults.data || [];

    if (stories.length > 0) {
      yield put(fetchNewStoriesSucceeded({
        stories,
        intervalOptions,
        intervalSelectionIndex,
        searchTerms,
        startDate,
        endDate,
        lineChartBackDisabled
      }));
    } else {
      const payload = {
        title: 'No results found',
        body: 'Please try another search.',
        type: 'info'
      };
      yield put(fetchFailed(payload));
    }
  } catch (e) {
    yield put(fetchFailed(e.message));
  }
};

// reset fetches the first page for a newly selected source and replaces the list
export const fetchMoreStories = function* ({ reset = false } = {}) {
  let meta;
  try {
    const lastSearchStartDate = yield select(getLastSearchStartDate);
    const lastSearchEndDate = yield select(getLastSearchEndDate);
    const hasMoreStories = yield select(getHasMoreStories);

    if (!hasMoreStories && !reset) {
      return;
    }
    const currentStories = yield select(getStories);

    const {
      zqlArray,
      startDate,
      endDate,
      searchTerms,
      selectedLanguages,
      additionalFilters
    } = yield _getQueryBody(true);
    // if there is an error then we won't search
    const errorCount = zqlArray.filter((zo) => zo.err).length;

    // this will put the parser errors on the store and will display them
    // in the discover2 UI, coordinate with Duncan for the design.
    yield put(setFFBParseErrors(zqlArray));

    if (errorCount > 0) {
      throw new Error('Invalid search terms');
    }
    // pendo panel tracking
    meta = _pendoPanelProps('Discover Historics Search', searchTerms, selectedLanguages, additionalFilters, startDate, endDate);

    const results = yield call(apiClient, STORIES_URL, _storiesRequest({
      zqlArray,
      startTime: lastSearchStartDate || startDate,
      endTime: lastSearchEndDate || endDate,
      from: reset ? 0 : currentStories.length
    }));

    yield put(fetchMoreStoriesSucceeded({
      stories: results.data || [],
      replace: reset
    }, meta));
  } catch (e) {
    // try to get error from body if it's there, otherwise use the message
    get(meta, 'analytics.eventPayload.properties', {}).error = get(e, 'body.error', e.message);
    yield put(fetchFailed(e.message, meta));
  }
};

export const fetchMoreStoriesSaga = function* () {
  while (true) {
    const backgroundFetch = yield takeLatest(FETCH_MORE_STORIES, fetchMoreStories);
    yield take(CLEAR_SEARCH_RESULTS);
    yield cancel(backgroundFetch);
  }
};

export const fetchSearchResultsSaga = function* () {
  while (true) {
    const backgroundFetch = yield takeLatest(FETCH_SEARCH_RESULTS, fetchSearchResults);
    yield take(CLEAR_SEARCH_RESULTS);
    yield cancel(backgroundFetch);
  }
};

export const fetchRecentSearchResultsSaga = function* () {
  while (true) {
    const backgroundFetch = yield takeLatest(FETCH_RECENT_SEARCH_RESULTS, fetchRecentSearchResults);
    yield take(CLEAR_SEARCH_RESULTS);
    yield cancel(backgroundFetch);
  }
};

export const fetchTranslationsSaga = function* () {
  yield takeLatest(FETCH_TRANSLATIONS, fetchTranslations);
};

export const fetchSearchIntervalSaga = function* () {
  while (true) {
    const backgroundFetch = yield takeLatest(FETCH_SEARCH_INTERVAL, fetchSearchInterval);
    yield take(CLEAR_SEARCH_RESULTS);
    yield cancel(backgroundFetch);
  }
};

export const fetchNewStoriesSaga = function* () {
  while (true) {
    const backgroundFetch = yield takeLatest(FETCH_NEW_STORIES, fetchNewStories);
    yield take(CLEAR_SEARCH_RESULTS);
    yield cancel(backgroundFetch);
  }
};

export const clearUserHistorySaga = function* () {
  try {
    yield take(CLEAR_RECENT_SEARCHES);
    const user = yield select(getUser);
    const url = `/api/discover-search-history/user/${user.id}`;
    const fetchOptions = {
      method: 'DELETE',
    };

    yield call(apiClient, url, fetchOptions);

    yield put(clearUserHistorySucceeded());
  } catch (e) {
    yield put(fetchFailed(e.message));
  }
};

export const saveAsProfileSaga = function* () {
  yield takeLatest(SAVE_AS_PROFILE, saveAsProfile);
};

const sagas = function* () {
  yield all([
    fork(fetchSearchResultsSaga),
    fork(fetchRecentSearchResultsSaga),
    fork(fetchTranslationsSaga),
    fork(fetchSearchIntervalSaga),
    fork(fetchRecentSearchesSaga),
    fork(updateSearchHistorySaga),
    fork(clearUserHistorySaga),
    fork(fetchMoreStoriesSaga),
    fork(fetchNewStoriesSaga),
    fork(saveAsProfileSaga)
  ]);
};

export default sagas;
export {
  DISCOVER_HISTORY_LIMIT,
  // protected
  _pendoPanelProps,
  _setPendopanelQueryTimer,
  _getIntervalOptions,
  _saveSearchHistory,
  _applyTwitterContentFilters,
  _applySourceSelection,
  _applyMediaFilters,
  _applyExcludeUSFilter,
  _parseSearchTerms,
  _isKeywordTarget,
  _generateKeywords,
  _generateTranslationState,
  _addTranslationsToSearchTerms,
  _matchesTranslationShape,
  _translationNotice,
  // public,
  getTranslations,
  getEnabledKeywordsCount,
  getSelectedLanguages,
  getEndDate,
  getSearchTerms,
  getStartDate,
  getTimezone,
  getUser,
  getPrivacyMode,
  getAdditionalFilters,
  getHasMoreStories,
  getStories,
};
